import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import { AdminShell } from "@/admin/AdminShell";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import BankIdCompletePage from "@/pages/BankIdCompletePage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import DashboardPage from "@/pages/DashboardPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import UploadPage from "@/pages/UploadPage";
import ManualInvoicePage from "@/pages/ManualInvoicePage";
import MonthlyPage from "@/pages/MonthlyPage";
import SuppliersPage from "@/pages/SuppliersPage";
import CategoriesPage from "@/pages/CategoriesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SettingsPage from "@/pages/SettingsPage";
import FamilyPage from "@/pages/FamilyPage";
import ProfilePage from "@/pages/ProfilePage";
import WorkspacesPage from "@/pages/WorkspacesPage";
import AttachmentsPage from "@/pages/bilag/AttachmentsPage";
import AttachmentDetailPage from "@/pages/bilag/AttachmentDetailPage";
import HelpPage from "@/pages/HelpPage";
import VaultPage from "@/pages/passord/VaultPage";
import SharedPage from "@/pages/passord/SharedPage";
import VaultSettingsPage from "@/pages/passord/VaultSettingsPage";
import HealthPage from "@/pages/passord/HealthPage";
import { VaultProvider } from "@/context/VaultContext";
import { TourProvider } from "@/context/TourContext";
import SalesDashboardPage from "@/pages/sales/SalesDashboardPage";
import SalesInvoicesPage from "@/pages/sales/SalesInvoicesPage";
import RecurringPage from "@/pages/sales/RecurringPage";
import RecurringEditorPage from "@/pages/sales/RecurringEditorPage";
import SalesRemindersPage from "@/pages/sales/SalesRemindersPage";
import SalesEmailLogPage from "@/pages/sales/SalesEmailLogPage";
import HouseholdOverviewPage from "@/pages/husholdning/HouseholdOverviewPage";
import HouseholdTransactionsPage from "@/pages/husholdning/HouseholdTransactionsPage";
import HouseholdYearPage from "@/pages/husholdning/HouseholdYearPage";
import HouseholdCategoriesPage from "@/pages/husholdning/HouseholdCategoriesPage";
import SalesInvoiceEditorPage from "@/pages/sales/SalesInvoiceEditorPage";
import SalesInvoiceDetailPage from "@/pages/sales/SalesInvoiceDetailPage";
import CustomersPage from "@/pages/sales/CustomersPage";
import ProductsPage from "@/pages/sales/ProductsPage";
import PaymentSettingsPage from "@/pages/sales/PaymentSettingsPage";
import PayPage from "@/pages/PayPage";
import AdminDashboardPage from "@/admin/AdminDashboardPage";
import AdminUsersPage from "@/admin/AdminUsersPage";
import AdminSettingsPage from "@/admin/AdminSettingsPage";
import AdminAuditLogPage from "@/admin/AdminAuditLogPage";
import AdminHealthPage from "@/admin/AdminHealthPage";
import AdminCleanupPage from "@/admin/AdminCleanupPage";

const POST_AUTH_KEY = "rp_post_auth_redirect";

/** Sender brukeren dit den skulle etter innlogging/registrering (f.eks. /familie etter invitasjonslenke).
 *  Nøkkelen leses i render og fjernes i en effekt – trygt også under StrictMode (dobbel render). */
const PostAuthRedirect = () => {
  const wanted = sessionStorage.getItem(POST_AUTH_KEY);
  useEffect(() => {
    if (wanted) sessionStorage.removeItem(POST_AUTH_KEY);
  }, [wanted]);
  return <Navigate to={wanted && wanted.startsWith("/") ? wanted : "/"} replace />;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    // Innlogget bruker åpner en invitasjonslenke: koble den til kontoen under Familie og fullmakt
    const invite = new URLSearchParams(location.search).get("invite");
    if (invite) return <Navigate to={`/familie?invite=${encodeURIComponent(invite)}`} replace />;
    return <PostAuthRedirect />;
  }
  return children;
};

/** Faktura-modulen og profilen er kun for kontoeier – ikke tilgjengelig når man handler på vegne av andre. */
const OwnerOnly = ({ children }) => {
  const { isOwner } = useAuth();
  if (!isOwner) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="rp-theme" disableTransitionOnChange>
        <AuthProvider>
          <VaultProvider>
          <TourProvider>
          <TooltipProvider delayDuration={200}>
            <BrowserRouter>
              <Routes>
                <Route path="/logg-inn" element={<PublicOnly><LoginPage /></PublicOnly>} />
                <Route path="/opprett-konto" element={<PublicOnly><RegisterPage /></PublicOnly>} />
                <Route path="/bankid/fullfor" element={<BankIdCompletePage />} />
                <Route path="/bekreft-epost" element={<VerifyEmailPage />} />
                {/* Offentlig betalingsside (kunden) – ingen innlogging */}
                <Route path="/betal/:token" element={<PayPage />} />
                <Route path="/betal/:token/retur" element={<PayPage />} />

                <Route element={<ProtectedRoute role="user"><AppShell /></ProtectedRoute>}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/fakturaer" element={<InvoicesPage />} />
                  <Route path="/fakturaer/:id" element={<InvoiceDetailPage />} />
                  <Route path="/last-opp" element={<UploadPage />} />
                  <Route path="/registrer" element={<ManualInvoicePage />} />
                  <Route path="/maanedsoversikt" element={<MonthlyPage />} />
                  <Route path="/leverandorer" element={<SuppliersPage />} />
                  <Route path="/kategorier" element={<CategoriesPage />} />
                  <Route path="/varsler" element={<NotificationsPage />} />
                  <Route path="/familie" element={<FamilyPage />} />
                  <Route path="/innstillinger" element={<SettingsPage />} />
                  {/* Bilagsbank – felles for Regninger og Faktura (virksomhetsdata) */}
                  <Route path="/bilag" element={<AttachmentsPage />} />
                  <Route path="/bilag/:id" element={<AttachmentDetailPage />} />
                  <Route path="/hjelp" element={<HelpPage />} />
                  {/* Regnskap (intern modulnøkkel «husholdning») – kontoplan per virksomhet; eier + fullmakt som for Regninger */}
                  <Route path="/regnskap" element={<HouseholdOverviewPage />} />
                  <Route path="/regnskap/transaksjoner" element={<HouseholdTransactionsPage />} />
                  <Route path="/regnskap/aar" element={<HouseholdYearPage />} />
                  <Route path="/regnskap/kategorier" element={<HouseholdCategoriesPage />} />
                  <Route path="/husholdning/*" element={<Navigate to="/regnskap" replace />} />
                  <Route path="/profil" element={<OwnerOnly><ProfilePage /></OwnerOnly>} />
                  <Route path="/virksomheter" element={<OwnerOnly><WorkspacesPage /></OwnerOnly>} />
                  <Route path="/faktura" element={<OwnerOnly><SalesDashboardPage /></OwnerOnly>} />
                  <Route path="/faktura/fakturaer" element={<OwnerOnly><SalesInvoicesPage /></OwnerOnly>} />
                  <Route path="/faktura/purringer" element={<OwnerOnly><SalesRemindersPage /></OwnerOnly>} />
                  <Route path="/faktura/epostlogg" element={<OwnerOnly><SalesEmailLogPage /></OwnerOnly>} />
                  <Route path="/faktura/gjentakende" element={<OwnerOnly><RecurringPage /></OwnerOnly>} />
                  <Route path="/faktura/gjentakende/ny" element={<OwnerOnly><RecurringEditorPage /></OwnerOnly>} />
                  <Route path="/faktura/gjentakende/:id/rediger" element={<OwnerOnly><RecurringEditorPage /></OwnerOnly>} />
                  <Route path="/faktura/fakturaer/ny" element={<OwnerOnly><SalesInvoiceEditorPage /></OwnerOnly>} />
                  <Route path="/faktura/fakturaer/:id/rediger" element={<OwnerOnly><SalesInvoiceEditorPage /></OwnerOnly>} />
                  <Route path="/faktura/fakturaer/:id" element={<OwnerOnly><SalesInvoiceDetailPage /></OwnerOnly>} />
                  <Route path="/faktura/kunder" element={<OwnerOnly><CustomersPage /></OwnerOnly>} />
                  <Route path="/faktura/produkter" element={<OwnerOnly><ProductsPage /></OwnerOnly>} />
                  <Route path="/faktura/betaling" element={<OwnerOnly><PaymentSettingsPage /></OwnerOnly>} />
                  <Route path="/passord" element={<OwnerOnly><VaultPage /></OwnerOnly>} />
                  <Route path="/passord/delt" element={<OwnerOnly><SharedPage /></OwnerOnly>} />
                  <Route path="/passord/helse" element={<OwnerOnly><HealthPage /></OwnerOnly>} />
                  <Route path="/passord/innstillinger" element={<OwnerOnly><VaultSettingsPage /></OwnerOnly>} />
                </Route>

                <Route path="/admin" element={<ProtectedRoute role="admin"><AdminShell /></ProtectedRoute>}>
                  <Route index element={<AdminDashboardPage />} />
                  <Route path="brukere" element={<AdminUsersPage />} />
                  <Route path="innstillinger" element={<AdminSettingsPage />} />
                  <Route path="revisjonslogg" element={<AdminAuditLogPage />} />
                  <Route path="drift" element={<AdminHealthPage />} />
                  <Route path="opprydding" element={<AdminCleanupPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
          </TooltipProvider>
          </TourProvider>
          </VaultProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
